/**
 * The Card Wall's card object — the signature element (§13.1).
 *
 * "each card rendered as a true 1.586:1 card object with its issuer-themed
 *  gradient, embossed-style last-4 and nickname, a subtle light sweep on
 *  hover, and a flip/expand into its detail view."
 *
 * The 1.586:1 is ISO/IEC 7810 ID-1 — the actual proportion of the plastic in
 * your wallet. The sheen and emboss live in CSS (styles/index.css) so they stay
 * consistent wherever a card is drawn.
 *
 * Deliberately restrained: no issuer logos, no fake chip art, no card-network
 * badges we'd have to hardcode per issuer (§2.1). The gradient IS the identity;
 * the lighting layered over it (and the pointer-tracking highlight from
 * `useTilt`) is what makes it read as metal.
 */

import { cn } from "@/lib/cn";
import { formatLast4, formatMoney } from "@/lib/format";
import type { Card } from "@/lib/api/client";

const FALLBACK_THEME = {
  gradient_from: "#1c2431",
  gradient_to: "#2b3546",
  foreground: "#f5f3ee",
};

interface CardObjectProps {
  card: Card;
  /** Live cycle spend, when known. Provisional by nature (§10.4). */
  cycleSpend?: string | null;
  outstanding?: string | null;
  className?: string;
  onClick?: () => void;
  /** Renders the network name if the user recorded one. Never inferred. */
  showFacts?: boolean;
}

export function CardObject({
  card,
  cycleSpend,
  outstanding,
  className,
  onClick,
  showFacts = true,
}: CardObjectProps) {
  const theme = card.theme ?? FALLBACK_THEME;
  const isClosed = card.status === "closed";
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      onClick={onClick}
      type={onClick ? "button" : undefined}
      aria-label={onClick ? `Open ${cardLabel(card)}` : undefined}
      className={cn(
        "card-object text-left",
        onClick && "cursor-pointer",
        // A closed card is still on the wall — history is kept (§6) — but it
        // reads as retired rather than pretending to be live.
        isClosed && "opacity-55 saturate-[0.4]",
        className,
      )}
      style={{
        // The stored gradient is the identity; the two radial layers on top are
        // lighting, identical for every card — a lit top-left edge and a
        // shadowed bottom-right, which is what makes a flat gradient read as
        // a slab of metal.
        backgroundImage: [
          "radial-gradient(130% 90% at 0% 0%, rgb(255 255 255 / 0.16), transparent 55%)",
          "radial-gradient(120% 100% at 100% 100%, rgb(0 0 0 / 0.38), transparent 60%)",
          `linear-gradient(140deg, ${theme.gradient_from} 0%, ${theme.gradient_to} 100%)`,
        ].join(", "),
        color: theme.foreground,
      }}
    >
      <div className="relative z-[3] flex h-full flex-col justify-between p-[7%]">
        {/* Top row: issuer + name + status */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-mono text-[10px] uppercase tracking-[0.14em] opacity-70">
              {card.issuer}
            </div>
            <div className="embossed mt-0.5 line-clamp-2 font-display text-[clamp(0.95rem,1.6vw,1.15rem)] font-semibold leading-tight tracking-[-0.02em]">
              {card.nickname || card.product_name || card.issuer}
            </div>
            {card.nickname && card.product_name && (
              <div className="truncate text-[11px] opacity-55">{card.product_name}</div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            {isClosed && (
              <span
                className="rounded-full border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider opacity-70"
                style={{ borderColor: `${theme.foreground}55` }}
              >
                Closed
              </span>
            )}
            {/* A discovered card is a stub the parser created — it needs the
                user's confirmation before it's trustworthy (§7.4). */}
            {card.source === "discovered" && (
              <span
                className="rounded-full border border-dashed px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider opacity-80"
                style={{ borderColor: `${theme.foreground}66` }}
                title="Found in a statement. Confirm its details."
              >
                Unconfirmed
              </span>
            )}
            {showFacts && card.network && (
              <span className="text-[11px] font-semibold italic opacity-70">{card.network}</span>
            )}
          </div>
        </div>

        {/* Bottom: the embossed last-4 and the one number that matters here */}
        <div className="flex items-end justify-between gap-3">
          <div className="embossed font-mono text-[clamp(0.85rem,1.4vw,1rem)] tracking-[0.2em] opacity-95">
            {formatLast4(card.last4)}
          </div>

          {outstanding != null ? (
            <div className="shrink-0 text-right">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] opacity-60">
                Outstanding
              </div>
              <div className="embossed font-display text-[clamp(0.95rem,1.6vw,1.1rem)] font-semibold tracking-[-0.02em]">
                {formatMoney(outstanding)}
              </div>
            </div>
          ) : cycleSpend != null ? (
            <div className="shrink-0 text-right">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] opacity-60">
                This cycle
              </div>
              {/* Live cycle spend includes provisional alert rows, so it is
                  marked as such wherever it appears (§2.7, §10.4). */}
              <div className="font-display text-[clamp(0.95rem,1.6vw,1.1rem)] font-semibold opacity-80">
                {formatMoney(cycleSpend)}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
}

export function cardLabel(card: Card): string {
  return card.nickname || [card.issuer, card.product_name].filter(Boolean).join(" ");
}

/** Skeleton in the card's own shape — the wall doesn't reflow when data lands. */
export function CardObjectSkeleton() {
  return <div className="skeleton aspect-card w-full rounded-card shadow-card" />;
}
